<template>
  <v-container :class="$vuetify.display.mobile ? 'py-1 px-1' : 'py-4'" fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="dark-card" elevation="4">
          <v-card-title :class="$vuetify.display.mobile ? 'pa-2 text-body-1' : 'pa-4 text-h5'">
            <v-icon :class="$vuetify.display.mobile ? 'me-1' : 'me-2'" color="cyan">mdi-account-edit</v-icon>
            <span class="text-white">{{ $t('student.profile.title') || 'Менің профилім' }}</span>
          </v-card-title>
          
          <v-card-text :class="$vuetify.display.mobile ? 'pa-2' : 'pa-4'">
            <v-form ref="formRef" v-model="valid">
              <v-row>
                <!-- Profile Photo -->
                <v-col cols="12" :md="$vuetify.display.mobile ? 12 : 4">
                  <v-card 
                    variant="outlined" 
                    :style="photoPreview ? `background-image: url(${photoPreview}); background-size: cover; background-position: center;` : ''"
                    :height="$vuetify.display.mobile ? 180 : 220"
                  >
                    <v-card-text class="text-center d-flex flex-column justify-center align-center" style="height: 100%; background: rgba(0,0,0,0.3);">
                      <v-icon v-if="!photoPreview" :size="$vuetify.display.mobile ? 60 : 80" color="grey-lighten-1">mdi-account-circle</v-icon>
                      
                      <v-file-input
                        v-model="photoFile"
                        :label="$t('student.profile.uploadPhoto') || 'Сурет жүктеу'"
                        accept="image/*"
                        prepend-icon="mdi-camera"
                        @change="onPhotoSelect"
                        :class="$vuetify.display.mobile ? 'mt-2' : 'mt-4'"
                        variant="outlined"
                        hide-input
                        hide-details
                        :density="$vuetify.display.mobile ? 'compact' : 'default'"
                      />
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Profile Information -->
                <v-col cols="12" :md="$vuetify.display.mobile ? 12 : 8">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="profileData.fio"
                        :label="$t('student.profile.fullName') || 'Толық аты'"
                        :rules="[rules.required]"
                        variant="outlined"
                        prepend-inner-icon="mdi-account"
                        :density="$vuetify.display.mobile ? 'compact' : 'default'"
                      />
                    </v-col>

                    <v-col cols="12" :sm="$vuetify.display.mobile ? 12 : 6">
                      <v-text-field
                        v-model="profileData.phone"
                        :label="$t('student.profile.phone') || 'Телефон'"
                        :rules="[rules.required, rules.phone]"
                        variant="outlined"
                        prepend-inner-icon="mdi-phone"
                        :density="$vuetify.display.mobile ? 'compact' : 'default'"
                        readonly
                      />
                    </v-col>

                    <v-col cols="12" :sm="$vuetify.display.mobile ? 12 : 6">
                      <v-text-field
                        v-model="profileData.email"
                        :label="$t('student.profile.email') || 'Email'"
                        :rules="[rules.email]"
                        variant="outlined"
                        prepend-inner-icon="mdi-email"
                        :density="$vuetify.display.mobile ? 'compact' : 'default'"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-select
                        v-model="profileData.instructorId"
                        :items="instructors"
                        item-title="fio"
                        item-value="id"
                        :label="$t('student.profile.instructor') || 'Менің инструкторым'"
                        variant="outlined"
                        prepend-inner-icon="mdi-account-tie"
                        clearable
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="profileData.notes"
                        :label="$t('student.profile.notes') || 'Қосымша ақпарат'"
                        variant="outlined"
                        prepend-inner-icon="mdi-note-text"
                        rows="3"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="profileData.emergencyContact"
                        :label="$t('student.profile.emergencyContact') || 'Жедел байланыс'"
                        variant="outlined"
                        prepend-inner-icon="mdi-phone-alert"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="profileData.birthDate"
                        :label="$t('student.profile.birthDate') || 'Туған күні'"
                        type="date"
                        variant="outlined"
                        prepend-inner-icon="mdi-calendar"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <!-- Progress Information -->
              <v-divider class="my-6" />
              <v-row>
                <v-col cols="12">
                  <h3 class="text-h6 mb-4">{{ $t('student.profile.progress') || 'Менің прогрессім' }}</h3>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card color="primary" variant="tonal">
                    <v-card-text class="text-center">
                      <v-icon size="40" class="mb-2">mdi-test-tube</v-icon>
                      <div class="text-h4">{{ stats.testsCompleted || 0 }}</div>
                      <div class="text-body-2">{{ $t('student.profile.testsCompleted') || 'Тест өткізді' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card color="success" variant="tonal">
                    <v-card-text class="text-center">
                      <v-icon size="40" class="mb-2">mdi-percent</v-icon>
                      <div class="text-h4">{{ stats.averageScore || 0 }}%</div>
                      <div class="text-body-2">{{ $t('student.profile.averageScore') || 'Орташа ұпай' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card color="warning" variant="tonal">
                    <v-card-text class="text-center">
                      <v-icon size="40" class="mb-2">mdi-clock-outline</v-icon>
                      <div class="text-h4">{{ stats.lessonsCompleted || 0 }}</div>
                      <div class="text-body-2">{{ $t('student.profile.lessonsCompleted') || 'Сабақ өтті' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card color="info" variant="tonal">
                    <v-card-text class="text-center">
                      <v-icon size="40" class="mb-2">mdi-trophy</v-icon>
                      <div class="text-h4">{{ stats.level || 1 }}</div>
                      <div class="text-body-2">{{ $t('student.profile.level') || 'Деңгей' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Action Buttons -->
              <v-row class="mt-6">
                <v-col cols="12" class="d-flex gap-3">
                  <v-btn
                    color="primary"
                    size="large"
                    :loading="saving"
                    :disabled="!valid"
                    @click="saveProfile"
                  >
                    <v-icon start>mdi-content-save</v-icon>
                    {{ $t('common.save') || 'Сақтау' }}
                  </v-btn>

                  <v-btn
                    variant="outlined"
                    size="large"
                    @click="resetForm"
                  >
                    <v-icon start>mdi-refresh</v-icon>
                    {{ $t('common.reset') || 'Қалпына келтіру' }}
                  </v-btn>

                  <v-spacer />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success/Error Snackbars -->
    <v-snackbar v-model="showSuccess" color="success">
      {{ successMessage }}
    </v-snackbar>

    <v-snackbar v-model="showError" color="error">
      {{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import { withBasePath } from '@/utils/path'

// Composables
const authStore = useAuthStore()
const { t } = useI18n()

// Refs
const formRef = ref(null)
const valid = ref(false)
const saving = ref(false)
const photoFile = ref(null)
const photoPreview = ref('')
const showSuccess = ref(false)
const showError = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Data
const profileData = reactive({
  fio: '',
  phone: '',
  email: '',
  instructorId: null,
  notes: '',
  emergencyContact: '',
  birthDate: '',
  photo: ''
})

const stats = reactive({
  testsCompleted: 0,
  averageScore: 0,
  lessonsCompleted: 0,
  level: 1
})

const instructors = ref([])

const resolvePhotoUrl = (value) => {
  if (!value) return ''
  if (/^(?:https?:)?\/\//i.test(value) || value.startsWith('data:')) {
    return value
  }

  const trimmed = value.replace(/^\/+/, '')

  if (trimmed.startsWith('uploads/')) {
    return withBasePath(`/${trimmed}`)
  }

  if (trimmed.startsWith('students/')) {
    return withBasePath(`/uploads/${trimmed}`)
  }

  return withBasePath(`/uploads/students/${trimmed}`)
}

// Validation rules
const rules = {
  required: v => !!v || t('validation.required') || 'Міндетті өріс',
  email: v => !v || /.+@.+\..+/.test(v) || t('validation.email') || 'Email форматы дұрыс емес',
  phone: v => !!v || t('validation.phone') || 'Телефон нөмері міндетті',
  minLength: v => !v || v.length >= 6 || t('validation.minLength', { min: 6 }) || 'Кемінде 6 символ'
}

// Methods
const loadProfile = async () => {
  try {
    const user = authStore.user
    if (user) {
      Object.assign(profileData, {
        fio: user.fio || '',
        phone: user.phone || '',
        email: user.email || '',
        instructorId: user.instructorId,
        photo: user.photo || ''
      })

      photoPreview.value = resolvePhotoUrl(profileData.photo)
    }

    // Load statistics
    const statsResponse = await api.get('/users/profile/stats')
    Object.assign(stats, statsResponse.data)
  } catch (error) {
    // Error
    showErrorMessage('Профиль мәліметтерін жүктеу қатесі')
  }
}

const loadInstructors = async () => {
  try {
    const response = await api.get('/public/instructors')
    instructors.value = response.data.instructors || []
  } catch (error) {
    // Error
  }
}

const onPhotoSelect = async (event) => {
  const file = photoFile.value?.[0]
  if (!file) return

  // Validate file size (5MB)
  if (file.size > 5 * 1024 * 1024) {
    showErrorMessage('Файл көлемі 5MB аспауы керек')
    return
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    showErrorMessage('Тек сурет файлдары ғана рұқсат етілген')
    return
  }

  // Show preview
  const reader = new FileReader()
  reader.onload = (e) => {
    photoPreview.value = e.target.result
  }
  reader.readAsDataURL(file)

  // Upload immediately
  await uploadPhoto(file)
}

const uploadPhoto = async (file) => {
  try {
    const formData = new FormData()
    formData.append('photo', file)

    const response = await api.post('/users/profile/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    profileData.photo = response.data.filename
    photoPreview.value = resolvePhotoUrl(response.data.url || response.data.filename)
    showSuccessMessage(response.data.message || 'Сурет сәтті жүктелді')
    if (authStore.user) {
      authStore.user = { ...authStore.user, photo: profileData.photo }
    }
    photoFile.value = null
  } catch (error) {
    // Error
    showErrorMessage('Сурет жүктеу қатесі')
  }
}

const saveProfile = async () => {
  if (!valid.value) return

  saving.value = true
  try {
    const response = await api.put('/users/profile', profileData)
    
    // Update auth store
    authStore.user = { ...authStore.user, ...response.data.user }
    
    showSuccessMessage('Профиль сәтті жаңартылды')
  } catch (error) {
    // Error
    showErrorMessage(error.response?.data?.error || 'Профильді сақтау қатесі')
  } finally {
    saving.value = false
  }
}

const resetForm = () => {
  loadProfile()
  photoFile.value = null
}

const showSuccessMessage = (message) => {
  successMessage.value = message
  showSuccess.value = true
}

const showErrorMessage = (message) => {
  errorMessage.value = message
  showError.value = true
}

// Lifecycle
onMounted(() => {
  loadProfile()
  loadInstructors()
})
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.dark-card {
  background: #2d2d2d !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.v-file-input {
  max-width: 200px;
}

/* Mobile Optimizations */
@media (max-width: 960px) {
  .v-container {
    padding: 4px !important;
  }
  
  .v-card {
    margin-bottom: 8px !important;
  }
  
  .v-card-title {
    padding: 8px !important;
    font-size: 0.9rem !important;
  }
  
  .v-card-text {
    padding: 8px !important;
  }
  
  .v-text-field {
    margin-bottom: 8px !important;
  }
  
  .v-file-input {
    max-width: 180px;
  }
  
  .v-row {
    margin: 0 !important;
  }
  
  .v-col {
    padding: 4px !important;
  }
}

@media (max-width: 600px) {
  .v-container {
    padding: 2px !important;
  }
  
  .v-card-title {
    padding: 6px !important;
    font-size: 0.8rem !important;
  }
  
  .v-card-text {
    padding: 6px !important;
  }
  
  .v-file-input {
    max-width: 160px;
  }
  
  .v-col {
    padding: 2px !important;
  }
}
</style>
